import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Stack,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  Circle as UnreadIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import type { Notification } from '../../types/notification';
import { NOTIFICATION_TYPE_CONFIG } from '../../types/notification';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onClose?: () => void;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete,
  onClose,
}) => {
  const navigate = useNavigate();
  const config = NOTIFICATION_TYPE_CONFIG[notification.type];

  const handleClick = () => {
    // Mark as read
    if (!notification.isRead) {
      onMarkAsRead(notification._id);
    }

    // Navigate if there's a link
    if (notification.linkType && notification.linkId) {
      const routes: Record<string, string> = {
        task: `/tasks?taskId=${notification.linkId}`,
        project: `/projects/${notification.linkId}`,
        note: `/notes?noteId=${notification.linkId}`,
        client: `/clients/${notification.linkId}`,
        reminder: `/reminders`,
      };

      const route = routes[notification.linkType];
      if (route) {
        navigate(route);
        onClose?.();
      }
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification._id);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        p: 2,
        cursor: notification.linkType ? 'pointer' : 'default',
        backgroundColor: notification.isRead ? 'transparent' : alpha(config.color, 0.05),
        borderLeft: `3px solid ${notification.isRead ? 'transparent' : config.color}`,
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: alpha(config.color, 0.08),
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {/* Icon */}
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            backgroundColor: config.bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: '1.1rem',
          }}
        >
          {config.icon}
        </Box>

        {/* Content */}
        <Box flex={1} minWidth={0}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography
              variant="subtitle2"
              fontWeight={notification.isRead ? 400 : 600}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {notification.title}
            </Typography>

            <Stack direction="row" alignItems="center" spacing={0.5}>
              {!notification.isRead && (
                <UnreadIcon
                  sx={{
                    fontSize: 8,
                    color: config.color,
                  }}
                />
              )}
              <IconButton
                size="small"
                onClick={handleDelete}
                sx={{ ml: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              mt: 0.25,
            }}
          >
            {notification.message}
          </Typography>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default NotificationItem;
